import React, { useEffect, useState } from "react";

import { useMutation } from "@apollo/client";
import { Box, Button, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import DeleteModal from "@/components/DeleteModal";
import { AddIconButton } from "@/components/IconButtons";
import { graphql } from "@/gql";
import useAlert from "@/hooks/useAlert";
import useDialogState from "@/hooks/useDialogState";
import { useFragment } from "@/hooks/useFragment";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";

import AddBodyCompositionModal from "./components/AddBodyCompositionModal";
import BodyCompositionDetails from "./components/BodyCompositionDetails";
import { clientProfilePageQuery } from "../../ClientProfilePage";

interface Props {
  clientId: string;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.white,
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(4),
  },
  section: {
    padding: theme.spacing(24),
    backgroundColor: colors.white,
    width: "100%",
  },
}));

const useButtonStyles = makeStyles()({
  root: {
    height: "2rem",
    marginRight: 8,
  },
  disabled: {
    border: 0,
  },
});

const bodyCompositionSectionFragment = graphql(`
  fragment BodyCompositionSection on Client {
    id
    journalGoal {
      id
      targetWeightKg
    }
    latestWeightKg {
      id
      value
    }
    setting {
      ...BodyCompositionDetails_Setting
    }
    latestBodyCompositions {
      ...BodyCompositionDetails
    }
  }
`);

const deleteBodyCompositionsMutation = graphql(`
  mutation DeleteBodyCompositions($input: DeleteBodyCompositionsInput!) {
    deleteBodyCompositions(input: $input) {
      client {
        id
      }
    }
  }
`);

const BodyCompositionSection: React.FC<Props> = ({ clientId }) => {
  const [client] = useFragment(
    bodyCompositionSectionFragment,
    `Client:${clientId}`
  );
  const [deleteBodyCompositions] = useMutation(deleteBodyCompositionsMutation);
  const { classes } = useStyles();
  const buttonClasses = useButtonStyles();
  const location = useLocation();
  const historyLink = "body-composition-history";
  const snackbar = useGlobalSnackbar();
  const alert = useAlert();
  const deleteDialog = useDialogState();

  const bodyCompositions = client?.latestBodyCompositions;

  const settings = client?.setting;

  const [modalVisibility, setModalVisibility] = useState<boolean>(false);
  const handleModalClose = () => {
    setCurrentDate(new Date());
    setIsEditing(false);
    setModalVisibility(false);
  };

  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const handleEdit = (date: Date) => {
    setIsEditing(true);
    setCurrentDate(date);
    setModalVisibility(true);
  };

  function getBodyCompositions() {
    if (bodyCompositions && settings) {
      return (
        <BodyCompositionDetails
          onDelete={deleteDialog.show}
          settings={settings}
          date={bodyCompositions.date}
          bodyCompositions={bodyCompositions}
          onEdit={handleEdit}
        />
      );
    } else {
      return <Typography>No data</Typography>;
    }
  }

  const handleModalOpen = () => {
    setModalVisibility(true);
  };

  useEffect(() => {
    if (location.hash === "#body-composition") {
      const element = document.getElementById("body-composition");
      element?.scrollIntoView();
    }
  }, [location]);

  const handleDelete = async () => {
    if (client == null || bodyCompositions == null) return;
    try {
      const result = await deleteBodyCompositions({
        variables: {
          input: {
            clientId: client.id,
            date: bodyCompositions.date,
          },
        },
        refetchQueries: [
          { query: clientProfilePageQuery, variables: { id: clientId } },
        ],
      });
      if (result.data) alert.open("Body compositions deleted successfully.");
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          "Something went wrong while deleting body compositions. Please try again later."
        )
      );
    }
  };

  return (
    <Box width="100%" className={classes.root} id="body-composition">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        my={2}
      >
        <Typography variant="h6" fontWeight="bold">
          Body Composition
        </Typography>

        <Box display="flex" alignItems="center">
          <AddIconButton classes={buttonClasses} onClick={handleModalOpen}>
            Add
          </AddIconButton>
          <Box marginX={1} />
          <Button
            classes={buttonClasses}
            component={Link}
            to={historyLink}
            color="primary"
            variant="outlined"
          >
            <Typography variant="subtitle2">View History</Typography>
          </Button>
        </Box>
      </Box>
      {getBodyCompositions()}
      <AddBodyCompositionModal
        isEditing={isEditing}
        settings={settings}
        visibility={modalVisibility}
        onClose={handleModalClose}
        currentDate={currentDate}
      />
      <DeleteModal
        visible={deleteDialog.shown}
        title=""
        message="Are you sure you want to delete body compositions for this date"
        onDelete={handleDelete}
        onClose={deleteDialog.hide}
      />
    </Box>
  );
};

export default BodyCompositionSection;
